import React from 'react';
import {
	useCreateNoteMutation,
	useUpdateNoteMutation,
} from '../../../../graphql/graphql';
import { ItemScheme } from '../../../../Components/Form/MagicFormItem';
import { MagicField } from '../../../../Enums/MagicField.enum';
import MagicForm from '../../../../Components/Form/MagicForm';
import useApiClient from '../../../Base/Hooks/useApiClient';
import useLoading from '../../../Base/Hooks/useLoading';

interface CaseNoteFormValues {
	note: string;
}

interface Props {
	caseId: string;
	note?: {
		id: string;
		text: string;
	};
	onSuccess?: () => void;
}

const FORM_SCHEME: ItemScheme[] = [
	{
		key: 'note',
		title: 'base.shared.note',
		type: MagicField.TEXTAREA,
		settings: {
			required: true,
			errorMessage: 'validation.caseNote.note',
		},
	},
];

const CaseNote: React.FC<Props> = ({ note, caseId, onSuccess }) => {
	const [createNote] = useCreateNoteMutation();
	const [updateNote] = useUpdateNoteMutation();
	const { displayGQLError, displayError } = useApiClient();
	const { isLoading, setIsLoading, RenderLoading } = useLoading();

	const handleCreate = async (values: CaseNoteFormValues) => {
		setIsLoading(true);
		try {
			const response = await createNote({
				caseId,
				text: values.note,
			}).unwrap();
			displayGQLError(response);
			if (response) {
				onSuccess?.();
			}
		} catch (e) {
			displayError(e);
		}
		setIsLoading(false);
	};

	const handleUpdate = async (values: CaseNoteFormValues) => {
		if (!note) return;
		setIsLoading(true);
		try {
			const response = await updateNote({
				id: note.id,
				text: values.note,
			}).unwrap();
			displayGQLError(response);
			if (response) {
				onSuccess?.();
			}
		} catch (e) {
			displayError(e);
		}
		setIsLoading(false);
	};

	const handleSubmit = async (values: CaseNoteFormValues) => {
		if (note) {
			await handleUpdate(values);
			return;
		}
		await handleCreate(values);
	};

	if (isLoading) {
		return <RenderLoading />;
	}

	return (
		<MagicForm
			formScheme={FORM_SCHEME}
			onSubmit={handleSubmit}
			submitText={note ? 'base.shared.edit' : 'base.create'}
			defaultValues={{ note: note?.text }}
		/>
	);
};

export default CaseNote;
